import {
  Add,
  Apartment,
  DeleteOutline,
  Gavel,
  AccountBalance,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import {
  useCreateCourtAdminUnitAssignment,
  useDeleteCourtAdminUnitAssignment,
} from '../../Aemter/hooks';
import { ApiError } from '../../hooks/useFetch';
import { useAlert } from '../../context/AlertContext';
import type { ICourt } from '../../Interfaces/ICourt';
import type { IAuthority } from '../../Interfaces/IAuthority';
import type { ICourtRole } from '../../Interfaces/ICourtRole';
import type { ICourtAdminstrativeUnitAssignment } from '../../Interfaces/ICourtAdminstrativeUnitAssignment';
import type { IAdminstrativeUnit } from '../../Interfaces/IAdminstrativeUnit';
import type { IFederalState } from '../../Interfaces/IAemter';
import { EditAdministrativeUnit } from './Edits/EditAdministrativeUnit';
import { EditCourt } from './Edits/EditCourt';

export type DetailMode = 'gemarkung' | 'court' | 'authority';

interface IProps {
  mode: DetailMode;
  entityId: number;
  federalStates: IFederalState[];
  courts: ICourt[];
  authorities: IAuthority[];
  administrativeUnits: IAdminstrativeUnit[];
  courtRoles: ICourtRole[];
  assignments: ICourtAdminstrativeUnitAssignment[];
  onAnyChange: () => void;
}

// Ein „Amt" ist ein Gericht ODER eine Behörde (#1208) – die Zuordnung zeigt
// je Rolle/Gemarkung auf genau eines davon.
type OfficeType = 'court' | 'authority';
interface Office {
  type: OfficeType;
  id: number;
  name: string;
  idFederalState?: number;
}

/** Backend-Fehlermeldung (`fehler`) aus einem ApiError ziehen, sonst Fallback. */
const extractErrorText = (e: unknown, fallback: string): string => {
  if (
    e instanceof ApiError &&
    e.body &&
    typeof e.body === 'object' &&
    'fehler' in e.body
  ) {
    const f = (e.body as { fehler?: unknown }).fehler;
    if (typeof f === 'string' && f) return f;
  }
  return fallback;
};

const officeKey = (o: Office) => `${o.type}:${o.id}`;
const roleCategoryForOffice = (t: OfficeType) => t; // 'court' -> 'court', 'authority' -> 'authority'

/**
 * Detail-Pane der Ämter-Workbench. Modi:
 *   • gemarkung: Gemarkung wählen → zuständige Ämter (Gerichte UND Behörden)
 *     je Rolle sehen/pflegen.
 *   • court/authority: Amt wählen → Gemarkungen sehen, in denen es Rollen hat.
 *
 * #1207/#1208: Rollen sind kategorisiert (court/authority); ein court-Amt kann
 * nur court-Rollen halten, ein authority-Amt nur authority-Rollen. Pro
 * (Gemarkung, Rolle) ist nur EIN Amt möglich (#1206).
 */
export const CourtAssignmentDetailPane: React.FC<IProps> = ({
  mode,
  entityId,
  federalStates,
  courts,
  authorities,
  administrativeUnits,
  courtRoles,
  assignments,
  onAnyChange,
}) => {
  const { showAlert } = useAlert();
  const { createCourtAdminUnitAssignment, isPendingCreateAdminUnitAssignment } =
    useCreateCourtAdminUnitAssignment();
  const { deleteCourtAdminstrativeUnitAssignment, isPending: isPendingDelete } =
    useDeleteCourtAdminUnitAssignment();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerCounterpartKey, setPickerCounterpartKey] = useState<string>('');
  const [pickerRoleId, setPickerRoleId] = useState<number | ''>('');

  const isGemarkungMode = mode === 'gemarkung';
  const ownOfficeType: OfficeType =
    mode === 'authority' ? 'authority' : 'court';

  const adminUnit = isGemarkungMode
    ? administrativeUnits.find(u => u.idAdministrativeUnit === entityId)
    : undefined;
  const court =
    mode === 'court' ? courts.find(c => c.idCourt === entityId) : undefined;
  const authority =
    mode === 'authority'
      ? authorities.find(a => a.idAuthority === entityId)
      : undefined;

  const fsName = (idFederalState?: number) =>
    federalStates.find(f => f.idFederalState === idFederalState)
      ?.FederalState ?? '';

  const ownFederalState = isGemarkungMode
    ? adminUnit?.idFederalState
    : mode === 'court'
      ? court?.idFederalState
      : authority?.idFederalState;

  const entityName = isGemarkungMode
    ? (adminUnit?.AdministrativeUnit ?? '–')
    : mode === 'court'
      ? (court?.Court ?? '–')
      : (authority?.Authority ?? '–');

  // Amt einer Zuordnung bestimmen (Behörde hat Vorrang, da Court dann NULL ist).
  const officeOfAssignment = (
    a: ICourtAdminstrativeUnitAssignment
  ): Office | null => {
    if (a.idAuthority) {
      const au = authorities.find(x => x.idAuthority === a.idAuthority);
      return au
        ? {
            type: 'authority',
            id: au.idAuthority,
            name: au.Authority,
            idFederalState: au.idFederalState,
          }
        : null;
    }
    if (a.idCourt) {
      const c = courts.find(x => x.idCourt === a.idCourt);
      return c
        ? {
            type: 'court',
            id: c.idCourt,
            name: c.Court,
            idFederalState: c.idFederalState,
          }
        : null;
    }
    return null;
  };

  // Zuordnungen dieser Entität.
  const ownAssignments = assignments.filter(a => {
    if (isGemarkungMode) return a.idAdministrativeUnit === entityId;
    if (mode === 'court') return a.idCourt === entityId;
    return a.idAuthority === entityId;
  });

  // #1206: Rollen, die in einer Gemarkung bereits an IRGENDEINEM Amt vergeben sind.
  const rolesUsedInGemarkung = (gemarkungId: number): Set<number> =>
    new Set(
      assignments
        .filter(
          a =>
            a.idAdministrativeUnit === gemarkungId &&
            a.activeAssignment !== false
        )
        .map(a => a.idCourtRole)
    );

  // Rollen passend zu einem Amtstyp und in der Gemarkung noch frei.
  const availableRolesFor = (
    officeType: OfficeType,
    gemarkungId: number
  ): ICourtRole[] => {
    const blocked = rolesUsedInGemarkung(gemarkungId);
    return courtRoles.filter(
      r =>
        r.activeRole !== false &&
        r.RoleCategory === roleCategoryForOffice(officeType) &&
        !blocked.has(r.idCourtRole)
    );
  };

  // Gruppierung der Zuordnungen nach Counterpart.
  const groupedByCounterpart = new Map<
    string,
    ICourtAdminstrativeUnitAssignment[]
  >();
  for (const a of ownAssignments) {
    let key: string;
    if (isGemarkungMode) {
      const o = officeOfAssignment(a);
      if (!o) continue;
      key = officeKey(o);
    } else {
      key = `au:${a.idAdministrativeUnit}`;
    }
    const arr = groupedByCounterpart.get(key) ?? [];
    arr.push(a);
    groupedByCounterpart.set(key, arr);
  }

  // Counterpart-Anzeige: im Gemarkung-Modus Ämter (Gericht/Behörde), sonst Gemarkungen.
  const allOffices: Office[] = [
    ...courts.map(c => ({
      type: 'court' as const,
      id: c.idCourt,
      name: c.Court,
      idFederalState: c.idFederalState,
    })),
    ...authorities.map(a => ({
      type: 'authority' as const,
      id: a.idAuthority,
      name: a.Authority,
      idFederalState: a.idFederalState,
    })),
  ];

  const counterpartName = (key: string): string => {
    if (isGemarkungMode) {
      const o = allOffices.find(x => officeKey(x) === key);
      return o?.name ?? key;
    }
    const id = Number(key.replace('au:', ''));
    return (
      administrativeUnits.find(u => u.idAdministrativeUnit === id)
        ?.AdministrativeUnit ?? key
    );
  };
  const counterpartOfficeType = (key: string): OfficeType | null =>
    key.startsWith('authority:')
      ? 'authority'
      : key.startsWith('court:')
        ? 'court'
        : null;

  // Für den „Neu zuordnen"-Picker verfügbare Counterparts (im selben Bundesland).
  const pickerCounterparts: Array<{ key: string; label: string }> =
    isGemarkungMode
      ? allOffices
          .filter(o => o.idFederalState === ownFederalState)
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(o => ({
            key: officeKey(o),
            label: `${o.name} (${o.type === 'court' ? 'Gericht' : 'Behörde'})`,
          }))
      : administrativeUnits
          .filter(u => u.idFederalState === ownFederalState)
          .sort((a, b) =>
            a.AdministrativeUnit.localeCompare(b.AdministrativeUnit)
          )
          .map(u => ({
            key: `au:${u.idAdministrativeUnit}`,
            label: u.AdministrativeUnit,
          }));

  // Zuordnung anlegen.
  const persistAssignment = async (
    officeType: OfficeType,
    officeId: number,
    gemarkungId: number,
    idCourtRole: number
  ) => {
    try {
      await createCourtAdminUnitAssignment({
        idAssignment: -1,
        idCourt: officeType === 'court' ? officeId : null,
        idAuthority: officeType === 'authority' ? officeId : null,
        idAdministrativeUnit: gemarkungId,
        idCourtRole,
        idFederalState: ownFederalState ?? -1,
        activeAssignment: true,
      });
      showAlert({ text: 'Rolle hinzugefügt' });
      onAnyChange();
    } catch (e) {
      console.error(e);
      showAlert({
        severity: 'error',
        text: extractErrorText(e, 'Fehler beim Speichern'),
      });
    }
  };

  // Rolle an bestehendem Counterpart-Card hinzufügen.
  const handleAddRoleToCounterpart = (key: string, idCourtRole: number) => {
    if (isGemarkungMode) {
      const oType = counterpartOfficeType(key);
      const oId = Number(key.split(':')[1]);
      if (!oType) return;
      void persistAssignment(oType, oId, entityId, idCourtRole);
    } else {
      const gemarkungId = Number(key.replace('au:', ''));
      void persistAssignment(ownOfficeType, entityId, gemarkungId, idCourtRole);
    }
  };

  const handleRemove = async (a: ICourtAdminstrativeUnitAssignment) => {
    try {
      await deleteCourtAdminstrativeUnitAssignment(a);
      showAlert({ text: 'Zuordnung entfernt' });
      onAnyChange();
    } catch (e) {
      console.error(e);
      showAlert({ severity: 'error', text: 'Fehler beim Entfernen' });
    }
  };

  const openPicker = () => {
    setPickerCounterpartKey('');
    setPickerRoleId('');
    setPickerOpen(true);
  };

  const submitPicker = async () => {
    if (!pickerCounterpartKey || typeof pickerRoleId !== 'number') return;
    if (isGemarkungMode) {
      const oType = counterpartOfficeType(pickerCounterpartKey);
      const oId = Number(pickerCounterpartKey.split(':')[1]);
      if (oType) await persistAssignment(oType, oId, entityId, pickerRoleId);
    } else {
      const gemarkungId = Number(pickerCounterpartKey.replace('au:', ''));
      await persistAssignment(
        ownOfficeType,
        entityId,
        gemarkungId,
        pickerRoleId
      );
    }
    setPickerOpen(false);
  };

  // Im Picker wählbare Rollen: abhängig vom gewählten Counterpart.
  const pickerRoleOptions: ICourtRole[] = (() => {
    if (!pickerCounterpartKey) return [];
    if (isGemarkungMode) {
      const oType = counterpartOfficeType(pickerCounterpartKey);
      if (!oType) return [];
      return availableRolesFor(oType, entityId);
    }
    const gemarkungId = Number(pickerCounterpartKey.replace('au:', ''));
    return availableRolesFor(ownOfficeType, gemarkungId);
  })();

  const usedCounterpartKeys = Array.from(groupedByCounterpart.keys()).sort(
    (a, b) => counterpartName(a).localeCompare(counterpartName(b))
  );

  const CounterpartIcon: React.FC<{ officeType?: OfficeType | null }> = ({
    officeType,
  }) => {
    if (!isGemarkungMode) return <Apartment fontSize='small' />;
    return officeType === 'authority' ? (
      <AccountBalance fontSize='small' />
    ) : (
      <Gavel fontSize='small' />
    );
  };

  return (
    <Box>
      {/* Stammdaten-Karte */}
      <Card variant='outlined' sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 2 }}>
            {isGemarkungMode ? (
              <Apartment color='primary' />
            ) : mode === 'court' ? (
              <Gavel color='primary' />
            ) : (
              <AccountBalance color='primary' />
            )}
            <Box>
              <Typography variant='h6'>{entityName}</Typography>
              <Typography variant='caption' color='text.secondary'>
                {fsName(ownFederalState)}
              </Typography>
            </Box>
          </Stack>

          {isGemarkungMode && adminUnit && (
            <EditAdministrativeUnit
              key={`adminunit-${adminUnit.idAdministrativeUnit}-${ownAssignments
                .map(a => a.idAssignment)
                .sort()
                .join(',')}`}
              currentAdminUnit={adminUnit}
              courtRoles={courtRoles}
              courtAdminUnitAssignments={assignments}
              onSuccess={onAnyChange}
            />
          )}
          {mode === 'court' && court && (
            <EditCourt
              key={`court-${court.idCourt}`}
              currentCourt={court}
              refetchCourts={onAnyChange}
            />
          )}
        </CardContent>
      </Card>

      {/* Zuordnungs-Karten */}
      <Card variant='outlined'>
        <CardContent>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            sx={{ mb: 1 }}
          >
            <Typography variant='subtitle1' fontWeight={600}>
              {isGemarkungMode
                ? 'Zuständige Stellen (Gerichte & Behörden) für diese Gemarkung'
                : mode === 'court'
                  ? 'Gemarkungen, in denen dieses Gericht Rollen innehat'
                  : 'Gemarkungen, in denen diese Behörde Rollen innehat'}
            </Typography>
            <Button
              size='small'
              variant='outlined'
              startIcon={<Add />}
              onClick={openPicker}
              disabled={ownFederalState === undefined}
            >
              {isGemarkungMode ? 'Stelle zuordnen' : 'Gemarkung zuordnen'}
            </Button>
          </Stack>

          {usedCounterpartKeys.length === 0 ? (
            <Alert severity='info' variant='outlined'>
              Noch keine Zuordnungen vorhanden.
            </Alert>
          ) : (
            <Stack spacing={1}>
              {usedCounterpartKeys.map(key => {
                const cpAssignments = groupedByCounterpart.get(key) ?? [];
                const oType = isGemarkungMode
                  ? counterpartOfficeType(key)
                  : ownOfficeType;
                // Zielgemarkung für die Rollen-Frei-Prüfung.
                const gemarkungId = isGemarkungMode
                  ? entityId
                  : Number(key.replace('au:', ''));
                const availableRoles = oType
                  ? availableRolesFor(oType, gemarkungId)
                  : [];
                return (
                  <Box
                    key={key}
                    sx={{
                      p: 1.25,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Stack
                      direction='row'
                      alignItems='center'
                      spacing={1}
                      sx={{ mb: 1 }}
                    >
                      <CounterpartIcon officeType={oType} />
                      <Typography
                        variant='body2'
                        fontWeight={600}
                        sx={{ flex: 1 }}
                      >
                        {counterpartName(key)}
                      </Typography>
                    </Stack>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {cpAssignments.map(a => {
                        const role = courtRoles.find(
                          r => r.idCourtRole === a.idCourtRole
                        );
                        return (
                          <Chip
                            key={a.idAssignment}
                            label={role?.CourtRole ?? `Rolle ${a.idCourtRole}`}
                            size='small'
                            color='primary'
                            variant='outlined'
                            onDelete={() => void handleRemove(a)}
                            disabled={isPendingDelete}
                            deleteIcon={
                              <Tooltip title='Zuordnung entfernen'>
                                <DeleteOutline />
                              </Tooltip>
                            }
                          />
                        );
                      })}
                      {availableRoles.length > 0 && (
                        <InlineRolePicker
                          roles={availableRoles}
                          onPick={idCourtRole =>
                            handleAddRoleToCounterpart(key, idCourtRole)
                          }
                          disabled={isPendingCreateAdminUnitAssignment}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}

          {ownFederalState === undefined && (
            <Alert severity='warning' sx={{ mt: 1 }}>
              Diese Entität hat kein Bundesland — Zuordnungen können nicht
              angelegt werden.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Picker für neue Zuordnung */}
      <Dialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>
          {isGemarkungMode ? 'Stelle zuordnen' : 'Gemarkung zuordnen'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label={isGemarkungMode ? 'Gericht / Behörde' : 'Gemarkung'}
              value={pickerCounterpartKey}
              onChange={e => {
                setPickerCounterpartKey(e.target.value);
                setPickerRoleId('');
              }}
            >
              {pickerCounterparts.map(cp => (
                <MenuItem key={cp.key} value={cp.key}>
                  {cp.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label='Rolle'
              value={pickerRoleId}
              onChange={e => setPickerRoleId(Number(e.target.value))}
              disabled={!pickerCounterpartKey}
              helperText={
                pickerCounterpartKey && pickerRoleOptions.length === 0
                  ? 'Keine passende, freie Rolle für diese Stelle.'
                  : undefined
              }
            >
              {pickerRoleOptions.map(r => (
                <MenuItem key={r.idCourtRole} value={r.idCourtRole}>
                  {r.CourtRole}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)}>Abbruch</Button>
          <Button
            variant='contained'
            onClick={() => void submitPicker()}
            disabled={
              !pickerCounterpartKey ||
              typeof pickerRoleId !== 'number' ||
              isPendingCreateAdminUnitAssignment
            }
          >
            {isPendingCreateAdminUnitAssignment ? (
              <CircularProgress size={18} />
            ) : (
              'Zuordnen'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ---- Inline-Rollen-Picker als kleiner Add-Button mit Menu
interface InlineRolePickerProps {
  roles: ICourtRole[];
  onPick: (idCourtRole: number) => void;
  disabled?: boolean;
}

const InlineRolePicker: React.FC<InlineRolePickerProps> = ({
  roles,
  onPick,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<number | ''>('');

  const submit = () => {
    if (typeof selected !== 'number') return;
    onPick(selected);
    setOpen(false);
    setSelected('');
  };

  return (
    <>
      <Chip
        size='small'
        variant='outlined'
        color='warning'
        label='Rolle hinzufügen'
        deleteIcon={<Add />}
        onDelete={() => setOpen(true)}
        onClick={() => setOpen(true)}
        disabled={disabled}
      />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Rolle wählen</DialogTitle>
        <DialogContent>
          <TextField
            select
            label='Rolle'
            value={selected}
            onChange={e => setSelected(Number(e.target.value))}
            fullWidth
            sx={{ mt: 1 }}
          >
            {roles.map(r => (
              <MenuItem key={r.idCourtRole} value={r.idCourtRole}>
                {r.CourtRole}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Abbruch</Button>
          <Button
            variant='contained'
            onClick={submit}
            disabled={typeof selected !== 'number'}
          >
            Hinzufügen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
